/**
 * Cohesive ChatKit — surface chrome: ChatSurface, ChatHead, ChatFab.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx. Deliberate
 * departure: ChatSurface does NOT own scrolling (the kit autoscrolled to the
 * bottom every render, which would fight the product's reverse infinite
 * pagination). The host owns the scroll container via scrollRef/onScroll.
 */
import { cva, type VariantProps } from "class-variance-authority";
import {
  Check,
  ChevronDown,
  Maximize2,
  MessagesSquare,
  Minimize2,
  Minus,
  Plus,
} from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const surfaceVariants = cva(
  "flex flex-col overflow-hidden bg-card text-card-foreground",
  {
    variants: {
      variant: {
        panel:
          "relative h-full w-full rounded-xl border border-n-10 shadow-elev-3",
        /**
         * Pinned, lifted surface. Defaults to full-bleed below a 56px global
         * bar; the insets are tokenized so a host can contain it to a pane:
         * --chat-expanded-top / --chat-expanded-right / --chat-expanded-width /
         * --chat-expanded-shadow.
         */
        expanded:
          "fixed bottom-0 left-auto right-[var(--chat-expanded-right,0px)] top-[var(--chat-expanded-top,57px)] z-[1000] w-full max-w-[var(--chat-expanded-width,100%)] border-0 shadow-[var(--chat-expanded-shadow,var(--elev-5))]",
        bare: "relative h-full w-full",
      },
    },
    defaultVariants: { variant: "panel" },
  },
);

export interface ChatSurfaceProps extends VariantProps<typeof surfaceVariants> {
  head?: React.ReactNode;
  composer?: React.ReactNode;
  children: React.ReactNode;
  /** Host-owned scroll container ref (pagination, scroll-to-bottom). */
  scrollRef?: React.Ref<HTMLDivElement>;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  /** Classes for the inner message column (defaults to the kit's gap-5 p-5). */
  contentClassName?: string;
  className?: string;
}

export function ChatSurface({
  variant,
  head,
  composer,
  children,
  scrollRef,
  onScroll,
  contentClassName,
  className,
}: ChatSurfaceProps) {
  return (
    <div className={cn(surfaceVariants({ variant }), className)}>
      {head}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden"
      >
        <div className={cn("flex flex-col gap-5 p-5", contentClassName)}>
          {children}
        </div>
      </div>
      {composer && (
        <div className="flex-shrink-0 px-3 pb-3 pt-3">{composer}</div>
      )}
    </div>
  );
}

export interface ChatHeadSession {
  id: string;
  name: string;
  /** Secondary line in the switcher row, e.g. "2h ago". */
  meta?: string;
  unread?: boolean;
}

/**
 * Compact chat header: the agent mark + session name + chevron form one
 * session-switcher trigger; metadata lives only in the switcher list.
 */
export function ChatHead({
  sessionName,
  icon,
  sessions,
  activeSessionId,
  onSelectSession,
  switcherFooter,
  onNewChat,
  onMinimize,
  expanded,
  onExpand,
  trailing,
  className,
}: {
  sessionName: React.ReactNode;
  /** Leading mark; defaults to the rust MessagesSquare. */
  icon?: React.ReactNode;
  sessions?: ChatHeadSession[];
  activeSessionId?: string;
  onSelectSession?: (id: string) => void;
  /** Slot at the bottom of the switcher list (e.g. an Archived link). */
  switcherFooter?: React.ReactNode;
  onNewChat?: () => void;
  onMinimize?: () => void;
  expanded?: boolean;
  onExpand?: () => void;
  /** Extra trailing controls (delete, pin, …) before expand/minimize. */
  trailing?: React.ReactNode;
  className?: string;
}) {
  const mark = icon ?? (
    <span className="inline-flex size-[18px] shrink-0 items-center justify-center text-rust">
      <MessagesSquare className="size-[18px]" />
    </span>
  );
  const title = (
    <>
      {mark}
      <span className="truncate font-sans text-sm font-medium text-n-90">
        {sessionName}
      </span>
    </>
  );
  return (
    <div
      className={cn(
        "flex h-12 flex-shrink-0 items-center gap-2.5 border-b border-n-10 px-5",
        className,
      )}
    >
      {sessions && sessions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title="Switch session"
              className="-mx-1.5 flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left outline-none transition-colors duration-150 ease-out hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust"
            >
              {title}
              <ChevronDown className="size-3.5 shrink-0 text-n-40" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[260px]">
            <DropdownMenuLabel>Sessions</DropdownMenuLabel>
            {sessions.map((s) => (
              <DropdownMenuItem
                key={s.id}
                onClick={() => onSelectSession?.(s.id)}
                className="h-auto flex-col items-start gap-0.5 py-2"
              >
                <span className="flex w-full items-center gap-2 font-medium text-n-90">
                  <span className="truncate">{s.name}</span>
                  {s.unread && (
                    <span className="size-1.5 shrink-0 rounded-full bg-rust" />
                  )}
                  {s.id === activeSessionId && (
                    <Check className="ml-auto size-3.5 shrink-0 text-rust" />
                  )}
                </span>
                {s.meta && (
                  <span className="font-sans text-xs text-n-50">{s.meta}</span>
                )}
              </DropdownMenuItem>
            ))}
            {switcherFooter}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex min-w-0 items-center gap-2">{title}</div>
      )}
      {onNewChat && (
        <Button
          variant="ghost"
          size="icon-sm"
          title="New chat"
          onClick={onNewChat}
        >
          <Plus />
        </Button>
      )}

      <span className="flex-1" />
      {trailing}
      {onExpand && (
        <Button
          variant="ghost"
          size="icon-sm"
          title={expanded ? "Collapse" : "Expand to full screen"}
          onClick={onExpand}
        >
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </Button>
      )}
      {onMinimize && (
        <Button
          variant="ghost"
          size="icon-sm"
          title="Minimize to a bubble"
          onClick={onMinimize}
        >
          <Minus />
        </Button>
      )}
    </div>
  );
}

/** Minimized chat — floating bubble (FAB), bottom-right, with unread badge. */
export function ChatFab({
  onClick,
  count = 0,
  title = "Open chat",
  className,
}: {
  onClick: () => void;
  count?: number;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "fixed bottom-6 right-6 z-[1000] inline-flex size-14 items-center justify-center rounded-full bg-rust text-paper shadow-elev-4 outline-none transition-[background-color,box-shadow] duration-150 ease-out hover:bg-[color-mix(in_oklab,var(--rust)_85%,var(--ink))] focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-2",
        className,
      )}
    >
      <MessagesSquare className="size-5" />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-card px-1 font-sans text-2xs font-semibold text-rust shadow-hairline">
          {count}
        </span>
      )}
    </button>
  );
}
